/* SHARED IS COMPUTE ENGINE
   Inputs: cutoff, actualsData, forecastData
   Outputs: data = { lineId: { month: value } } */

import { daLeafIds } from './daRouting';

const r2 = n => Math.round(n * 100) / 100;

// ★ SWEEP B2-ii: the hardcoded 2024-2026 ALL_M window is DELETED. It was Semitek's
// model window used as a back-compat default; every caller passes allMonths
// (runFullModel always does) and no page imports the export — verified. An
// absent window now yields an empty one (honest-empty), not a foreign calendar.

export function computeIS(cutoff, actualsData, forecastData, miscRevenueLines, implementationLines, allMonths, isLines) {
  if (!isLines) throw new Error('computeIS requires isLines (per-tenant chart of accounts from buildISLinesForCompany)');
  const MONTHS = allMonths || [];
  const LINES = isLines;
  /* W-089 (2026-10-01): THE MISC `miscIds` LIST IS GONE -- the same fix N-A made for implementation (below).
   * It read:
   *     const miscIds = (miscRevenueLines || []).filter(l => l.enabled && !l.target_gl_line_key).map(l => `rev_misc_${l.id}`);
   * with its own data loop and an append to total_revenue's sumOf. buildISLines now EMITS rev_misc_<id> for an unrouted
   * line, so the leaf is in line.sumOf and Pass 1 builds its series; left in, this list would count the line TWICE
   * (measured in diffs/group3-build.txt). `miscRevenueLines` stays in the SIGNATURE: it is positional. */
  /* ═══ ★★ N-A — THE IMPLEMENTATION `implIds` LIST IS GONE, AND THAT IS THE WHOLE FIX ═══
   *
   * It read:
   *     const implIds = (implementationLines || []).filter(l => l.enabled).map(l => `rev_impl_${l.id}`);
   * and was (a) given its own data-line loop below and (b) appended to total_revenue's
   * sumOf beside miscIds. Both are deleted.
   *
   * ⛔⛔ IT DOUBLE-COUNTED AN IMPLEMENTATION LINE — EXACTLY 2× — ON *BOTH* ARMS, and the
   *   two arms did NOT share a mechanism:
   *     UNROUTED: ONE deposit, TWO SUMMERS. Part C (`f2d3d38`) made buildISLines EMIT the
   *       rev_impl_<id> leaf, so the key entered `line.sumOf`; this list appended the SAME
   *       id again, so the effective sumOf held it twice. (Measured on cid=36:
   *       [... rev_other_revenue, rev_impl_7, rev_impl_7].)
   *     ROUTED:   TWO deposits, ONE SUMMER EACH. runFullModel:809 deposits at the target
   *       (an existing leaf, already in line.sumOf) while orchestrator.js deposited a
   *       SECOND copy at the bare rev_impl_<id>, which this list then summed — money
   *       counted inside total_revenue and rendered on NO row.
   *   ⇒ a fix to either one alone closes ONE arm. This removal closes the unrouted arm;
   *     the orchestrator's deposit is made target-aware in the same commit, which closes
   *     the routed one at its source rather than leaving a stray writer.
   *
   * ★★ THE OWNER, NAMED: `buildISLines`' implLeaves emission (buildISLines.js:362-374) is
   *   the SINGLE site that decides whether an implementation line contributes a leaf to
   *   total_revenue.sumOf — and it is already the site that expresses the routed
   *   suppression (`l.enabled && !l.target_gl_line_key`). computeIS no longer second-
   *   guesses it. One owner, not two agreeing lists.
   *
   * ⛔ SUPPRESSING THE BUILDER'S LEAF WAS THE REJECTED CANDIDATE. It would have reverted
   *   Part C (the page-splice defect) and broken Part B's suppression-when-routed, which
   *   is expressed in that same filter. ⛔ A dedupe here (`implIds.filter(id => !sumOf
   *   .includes(id))`) was rejected too: two owners reconciled at the point of use is the
   *   drift shape §6 exists to forbid. ⛔ And no subtraction was added to cancel an
   *   addition — the money is added ONCE, by one owner.
   *
   * ⚠ THIS MAKES `isLines` LOAD-BEARING FOR IMPLEMENTATION REVENUE: a caller that builds
   *   its skeleton WITHOUT `implementationLines` (buildISLinesForCompany's 7th arg) now
   *   loses the money rather than double-counting it. Production passes it
   *   (ModelProvider.js:507); `verify-fullmodel.js` did NOT, and is aligned in the same
   *   pass. ⛔ Any new caller must pass it.
   *
   * ⓘ `implementationLines` stays in the SIGNATURE — it is POSITIONAL, and removing it
   *   would silently shift `allMonths` and `isLines` at all four call sites.
   * (W-089, 2026-10-01: the coupling below was honoured -- the builder emits the misc leaf and miscIds died in the same
   *  commit. The note follows as it stood.)
   * ⓘ MISC IS NOT TOUCHED, AND ITS `miscIds` ABOVE IS LOAD-BEARING: buildISLines emits NO
   *   rev_misc_<id> leaf for an UNROUTED misc line (IncomeStatementPage splices that row),
   *   so miscIds is misc's ONLY summer and is correct. ⛔ THE COUPLING: if queue item T3
   *   ever moves that splice into the builder, miscIds must die in the SAME commit or misc
   *   inherits this exact defect. Misc is LIVE on all four tenants. */

  const data = {};

  // Pass 1: data lines (static + misc)
  LINES.forEach(line => {
    if (line.type === "header" || line.type === "total" || line.type === "calc" || line.type === "margin") return;
    data[line.id] = {};
    MONTHS.forEach(m => {
      const pool = m <= cutoff ? actualsData : forecastData;
      data[line.id][m] = r2(pool[line.id]?.[m] || 0);
    });
  });
  /* ⛔ N-A — THE IMPLEMENTATION DATA-LINE LOOP IS GONE TOO, not only the sum.
   *   An UNROUTED line's rev_impl_<id> is an emitted leaf, so Pass 1 above already builds
   *   its series. A ROUTED line has no such leaf, and this loop was manufacturing a
   *   PHANTOM `isData.rev_impl_<id>` out of the orchestrator's stray deposit — measured
   *   present and POPULATED on the routed arm. Removing it removes the phantom key. */

  // Pass 2: totals (with misc IDs added to total_revenue sumOf)
  LINES.forEach(line => {
    if (line.type === "total" && line.sumOf) {
      data[line.id] = {};
      /* ⛔ `...implIds` REMOVED (N-A). Implementation ids reach this sum through
         `line.sumOf` — i.e. through the builder — or not at all. See the note at the top. */
      const sumOf = line.sumOf;   // W-089: misc ids reach this sum through the builder, like implementation's
      MONTHS.forEach(m => {
        if (m > cutoff && forecastData[line.id]?.[m] !== undefined) {
          data[line.id][m] = r2(forecastData[line.id][m]);
        } else {
          data[line.id][m] = r2(sumOf.reduce((s, id) => s + (data[id]?.[m] || 0), 0));
        }
      });
    }
  });

  // Pass 3: calcs (gross profit, operating income, net income, EBITDA)
  // W-075: the D&A leaves EBITDA adds back, by stored role (daRouting.daLeafIds), from THIS skeleton.
  // This is the only place a calc is invoked, so every calc receives the context.
  const calcCtx = { daIds: daLeafIds(LINES) };
  LINES.forEach(line => {
    if (line.type === "calc" && line.calc) {
      data[line.id] = {};
      MONTHS.forEach(m => { data[line.id][m] = line.calc(data, m, calcCtx); });
    }
  });

  return data;
}

export { r2 };
